import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import moment from 'moment';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { API_URL } from '../../../utils/constant';
import { customTableStyles } from '../../../utils/tableStylings';

const ClaimBills = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    customer: '',
    invoiceNumber: '',
    type: ''
  });
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    fetchClaims();
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const company = JSON.parse(localStorage.getItem('company'));
      const companyId = company?.company?.id;
      const response = await axios.get(`${API_URL}/customers/${companyId}`);
      if (response.data.data) {
        setCustomers(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchClaims = async () => {
    try {
      setLoading(true);
      const company = JSON.parse(localStorage.getItem('company'));
      const companyId = company?.company?.id;

      const response = await axios.get(`${API_URL}/pos/claim/list`);

      if (response.data.status) {
        setClaims(response.data.data);
      } else {
        Swal.fire('Error!', 'Failed to fetch claims', 'error');
      }
    } catch (err) {
      console.error('Error fetching claims:', err);
      Swal.fire('Error!', 'Error fetching claims', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchClaimsWithFilters = async () => {
    try {
      setLoading(true);
      const company = JSON.parse(localStorage.getItem('company'));
      const companyId = company?.company?.id;

      // Build query string from filters
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.customer) params.append('customerId', filters.customer);
      if (filters.invoiceNumber) params.append('invoiceNumber', filters.invoiceNumber);
      if (filters.type) params.append('type', filters.type);
      if (companyId) params.append('company_id', companyId);

      const response = await axios.get(`${API_URL}/pos/claim/list?${params.toString()}`);

      if (response.data.status) {
        setClaims(response.data.data);
        Swal.fire('Success!', `Found ${response.data.data.length} claims.`, 'success');
      } else {
        Swal.fire('Error!', 'Failed to fetch claims', 'error');
      }
    } catch (err) {
      console.error('Error fetching claims:', err);
      Swal.fire('Error!', 'Error fetching claims', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': 'bg-warning text-dark',
      'approved': 'bg-success',
      'rejected': 'bg-danger',
      'completed': 'bg-info'
    };
    return statusMap[status] || 'bg-secondary';
  };

  const getTypeLabel = (type) => {
    return type === 'refund' ? 'Money Back' : 'Exchange';
  };

  const handleApprove = async (claim) => {
    const result = await Swal.fire({
      title: 'Approve Claim?',
      text: `Approve ${getTypeLabel(claim.type)} claim for ${claim.product_name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, approve',
      cancelButtonText: 'No',
    });

    if (!result.isConfirmed) return;

    try {
      const response = await axios.put(`${API_URL}/pos/claim/approve/${claim.id}`, {});

      if (response.data.success) {
        Swal.fire('Approved!', 'Claim has been approved.', 'success');
        fetchClaims();
      } else {
        Swal.fire('Error!', response.data.message || 'Failed to approve claim', 'error');
      }
    } catch (err) {
      Swal.fire('Error!', 'Error approving claim', 'error');
      console.error(err);
    }
  };

  const handleReject = async (claim) => {
    const { value: reason } = await Swal.fire({
      title: 'Reject Claim',
      input: 'textarea',
      inputLabel: 'Rejection Reason',
      inputPlaceholder: 'Enter reason for rejection...',
      inputAttributes: {
        'aria-label': 'Enter reason for rejection'
      },
      showCancelButton: true,
      confirmButtonText: 'Reject',
      cancelButtonText: 'Cancel',
    });

    if (!reason) return;

    try {
      const response = await axios.put(`${API_URL}/pos/claim/reject/${claim.id}`, {
        reason: reason
      });

      if (response.data.success) {
        Swal.fire('Rejected!', 'Claim has been rejected.', 'success');
        fetchClaims();
      } else {
        Swal.fire('Error!', response.data.message || 'Failed to reject claim', 'error');
      }
    } catch (err) {
      Swal.fire('Error!', 'Error rejecting claim', 'error');
      console.error(err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitFilters = () => {
    fetchClaimsWithFilters();
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      customer: '',
      invoiceNumber: '',
      type: ''
    });
    setSearch('');
    fetchClaims();
  };

  const columns = [
    {
      name: '#',
      cell: (row, index) => index + 1,
      width: '50px'
    },
    {
      name: 'Invoice No.',
      selector: row => row.SalesBill?.bill_no,
      sortable: true,
      width: '100px'
    },
    {
      name: 'Customer',
      cell: row => (
        <div style={{ display: 'block' }}>
          <div><strong>{row.SalesBill?.Customer?.name || 'N/A'}</strong></div>
          <div className="text-muted">{row.SalesBill?.Customer?.phone || '-'}</div>
        </div>
      )
    },
    {
      name: 'Product / Variant',
      cell: row => (
        <div className="d-flex flex-column gap-1">
          {row.SalesBill?.is_other != 1 && row.Variants?.length ? (
            row.Variants.map(v => (
              <div key={v.id} className='badge bg-primary'>
                {/* Product name */}
                <div className="fw-semibold">
                  {v.FinalProduct?.name || 'N/A'}
                </div>

                {/* Variant badge */}
                <div className="d-flex flex-wrap gap-1 mt-1">
                  <span className="badge bg-white text-dark">
                    {v.variant_name} &nbsp;
                    {v.serial_no && (
                      <span className="text-dark">
                        ({v.serial_no})
                      </span>
                    )}
                  </span>
                </div>
              </div>
            ))
          ) : (
            row.SalesBill?.product_data?.map((s, index) => (
              s.serial_nos?.map(serial => (
                <div key={serial.serial_no || index} className="badge bg-primary">
                  <div className="fw-semibold">{s.product_name || 'N/A'} <span className='text-warning'>({s.product_type})</span></div>
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    <span className="badge bg-white text-dark">
                      {serial.serial_no && `${serial.serial_no}`}
                    </span>
                  </div>
                </div>
              ))
            ))

          )}
        </div>
      )
    },
    {
      name: 'Type',
      cell: row => (
        <span className="badge bg-primary">
          {getTypeLabel(row.type)}
        </span>
      ),
      width: '100px'
    },
    {
      name: 'Amount',
      selector: row => {
        if (row.type === 'refund') {
          return `₹${Number(row.amount || 0).toFixed(2)}`;
        } else if (row.Variants?.length) {
          const variantSum = row.Variants.reduce(
            (sum, v) => sum + Number(v.price || 0),
            0
          );
          return `₹${variantSum.toFixed(2)}`;
        } else {
          return `₹0.00`;
        }
      },
      right: true,
      width: '120px'
    },
    {
      name: 'Date',
      cell: row => moment(row.createdAt).format('DD-MM-YYYY hh:mm A'),
      sortable: true,
      width: '180px'
    }
  ];

  const filteredClaims = claims.filter(claim => {
    const searchTerm = search.toLowerCase();

    // Only apply search filter (client-side)
    if (searchTerm) {
      const matchSearch = (
        (claim.id && claim.id.toString().includes(searchTerm)) ||
        (claim.SalesBill?.bill_no && claim.SalesBill.bill_no.toLowerCase().includes(searchTerm)) ||
        (claim.FinalProductStockVariation?.FinalProduct?.name && claim.FinalProductStockVariation.FinalProduct.name.toLowerCase().includes(searchTerm)) ||
        (claim.FinalProductStockVariation?.serial_no && claim.FinalProductStockVariation.serial_no.toLowerCase().includes(searchTerm)) ||
        (claim.SalesBill?.Customer?.name && claim.SalesBill.Customer.name.toLowerCase().includes(searchTerm)) ||
        (claim.SalesBill?.Customer?.phone && claim.SalesBill.Customer.phone.includes(searchTerm))
      );
      if (!matchSearch) return false;
    }

    return true;
  });

  return (

    <>
      <div className="card" style={{ marginTop: "60px"}}>
        <div className="card-header  d-flex justify-content-between align-items-center">
          <h3 className='text-primary'>Claim Bills</h3>
        </div>
        <div className="card-header d-flex align-items-end flex-wrap gap-2">
          <div className="position-relative mt-3 mb-2">
            <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                Start Date
              </span>
            <input
              type="date"
              className="form-control"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </div>

          <div className="position-relative mt-3 mb-2">
            <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                End Date
              </span>
            <input
              type="date"
              className="form-control"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </div>

          <div className="position-relative mt-3 mb-2">
            <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                Invoice Number
              </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search invoice..."
              name="invoiceNumber"
              value={filters.invoiceNumber}
              onChange={handleFilterChange}
            />
          </div>

          <div className="position-relative mt-3 mb-2">
            <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2" style={{ zIndex: '1'}}>
                Customer
              </span>
            <Select
              name="customer"
              options={[
                { value: '', label: '-- All Customers --' },
                ...customers
                  .filter(customer => customer.name && customer.name.trim() !== '')
                  .map(customer => ({
                    value: customer.id,
                    label: `${customer.name} (${customer.phone})`
                  }))
              ]}
              value={
                filters.customer !== ''
                  ? {
                    value: filters.customer,
                    label: (() => {
                      const customer = customers.find(c => c.id == filters.customer);
                      return customer ? `${customer.name} (${customer.phone})` : '-- All Customers --';
                    })()
                  }
                  : { value: '', label: '-- All Customers --' }
              }
              onChange={(option) => handleFilterChange({
                target: { name: 'customer', value: option?.value || '' }
              })}
              isClearable={true}
              isSearchable={true}
              placeholder="Search customer..."
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '38px',
                  width: "370px"
                })
              }}
            />
          </div>

          <div className="position-relative mt-3 mb-2">
            <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                Type
              </span>
            <select
              className="form-select"
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
            >
              <option value="">-- All Types --</option>
              <option value="refund">Money Back</option>
              <option value="exchange">Exchange</option>
            </select>
          </div>

          <button
            className="btn btn-primary"
            onClick={handleSubmitFilters}
          >
            <i className="fas fa-search"></i> Apply Filters
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleResetFilters}
          >
            <i className="fas fa-redo"></i> Reset
          </button>
        </div>
        <div className="card-body">
          <DataTable
            columns={columns}
            data={filteredClaims}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 25, 50]}
            highlightOnHover
            striped
            progressPending={loading}
            progressComponent={<div className="p-3">Loading...</div>}
            noDataComponent={<div className="p-3">No claims found</div>}
            responsive
            customStyles={customTableStyles}
          />
        </div>
      </div>
    </>
  );
};

export default ClaimBills;